// Tabs for detail pages that carry more than one job.
//
// A booking detail is not one page: it answers "who is this and what did they
// book", "did the money work", "what happened", and "what do I do now". Stacked
// vertically those read as one 2,400px wall where nothing signals priority, so
// an operator scrolls past the thing they came for.
//
// Deliberately NOT url-routed. A tab here is a lens on one record, not a
// location — /bookings/:ref stays the address of the whole booking, and an
// operator sharing that link should not silently also share "and I was looking
// at the money". Keep tab state local; if deep-linking is ever needed, add a
// search param rather than splitting the route.
//
// Tabs whose content is absent must be omitted by the CALLER, not rendered
// empty: a tab that opens onto nothing is worse than no tab, because the
// operator pays a click to learn there was nothing there.

import { useId, useRef, useState, type ReactNode } from 'react';

export interface DetailTab {
  id: string;
  label: string;
  /** Rendered after the label — a count, or an alert dot for "needs action". */
  badge?: ReactNode;
  content: ReactNode;
}

interface DetailTabsProps {
  tabs: DetailTab[];
  /** Defaults to the first tab. */
  initial?: string;
}

export function DetailTabs({ tabs, initial }: DetailTabsProps) {
  const baseId = useId();
  const [active, setActive] = useState(() => initial ?? tabs[0]?.id ?? '');
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  // A tab list that cannot be driven from the keyboard is a mouse trap for
  // anyone working a queue at speed. Arrows move, Home/End jump — per the
  // WAI-ARIA tabs pattern.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.id === active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    const target = tabs[next];
    if (!target) return;
    setActive(target.id);
    refs.current[target.id]?.focus();
  };

  const current = tabs.find((t) => t.id === active) ?? tabs[0];
  if (!current) return null;

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Sections"
        onKeyDown={onKeyDown}
        // Scrolls rather than wraps: a wrapped tab strip reflows the content
        // below it as tabs change, which makes the page feel unstable.
        //
        // A tray around the pills, rather than a bottom rule with an underlined
        // tab: the underline version distinguished the active tab by a 2px line
        // and a grey-vs-black text colour, which was too weak to read at a
        // glance — and the line silently collapsed to 0px anyway because the
        // base `border-b-2` lost to the later `border-transparent`.
        className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] p-1"
      >
        {tabs.map((t) => {
          const selected = t.id === current.id;
          return (
            <button
              key={t.id}
              ref={(el) => {
                refs.current[t.id] = el;
              }}
              role="tab"
              type="button"
              id={`${baseId}-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${t.id}`}
              // Only the active tab is in the tab order; arrows move within.
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(t.id)}
              // Three signals at once for the active tab — white fill, a
              // shadow lifting it off the tray, and heavier/darker text — so it
              // reads without hunting. Contrast alone was not enough.
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-[13px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--bo-accent)] ${
                selected
                  ? 'bg-white font-semibold text-[color:var(--bo-ink)] shadow-sm'
                  : 'font-medium text-[color:var(--bo-mute)] hover:bg-black/[0.04] hover:text-[color:var(--bo-ink)]'
              }`}
            >
              {t.label}
              {t.badge}
            </button>
          );
        })}
      </div>

      {/* Only the active panel is mounted. These panels hold live forms
          (confirm, request payment) — keeping them all mounted would let a
          half-filled form sit invisible behind another tab. */}
      <div
        role="tabpanel"
        id={`${baseId}-panel-${current.id}`}
        aria-labelledby={`${baseId}-tab-${current.id}`}
        tabIndex={0}
        className="focus:outline-none"
      >
        {current.content}
      </div>
    </div>
  );
}
